/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import * as graphic from 'echarts/lib/util/graphic';
import {
  enterEmphasis,
  leaveEmphasis,
  toggleHoverEmphasis,
  setStatesStylesFromModel
} from 'echarts/lib/util/states';
import { LayoutOrient, ECElement } from 'echarts/lib/util/types';
import { PathProps } from 'zrender/lib/graphic/Path';
import SankeySeriesModel, {
  SankeyEdgeItemOption,
  SankeyNodeItemOption
} from 'echarts/lib/chart/sankey/sankeySeries';
import ChartView from 'echarts/lib/view/Chart';
import GlobalModel from 'echarts/lib/model/Global';
import ExtensionAPI from 'echarts/lib/core/ExtensionAPI';
import SeriesData from 'echarts/lib/data/SeriesData';
import { RectLike } from 'zrender/lib/core/BoundingRect';
import { setLabelStyle, getLabelStatesModels, labelInner } from 'echarts/lib/label/labelStyle';
import { getECData } from 'echarts/lib/util/innerStore';
import { isString } from 'zrender/lib/core/util';
import SankeyView from 'echarts/lib/chart/sankey/SankeyView';
import { getDefaultInterpolatedLabel, getDefaultLabel } from 'echarts/lib/chart/helper/labelHelper';

class SankeyPathShape {
  x1 = 0;

  y1 = 0;

  x2 = 0;

  y2 = 0;

  cpx1 = 0;

  cpy1 = 0;

  cpx2 = 0;

  cpy2 = 0;

  extent = 0;

  orient: LayoutOrient;
}

interface SankeyPathProps extends PathProps {
  shape?: Partial<SankeyPathShape>;
}

class SankeyPath extends graphic.Path<SankeyPathProps> {
  static shape: SankeyPathShape;

  constructor(opts?: SankeyPathProps) {
    super(opts);
  }

  getDefaultShape() {
    return new SankeyPathShape();
  }

  buildPath(ctx: CanvasRenderingContext2D, shape: SankeyPathShape) {
    if (shape.dvKind == 'sameLayerCurve') {
      const { extent } = shape;
      const curveHeight = extent > 10 ? 10 : extent;
      if (shape.orient === 'vertical') {
        ctx.moveTo(shape.x1, shape.y1);
        ctx.bezierCurveTo(
          shape.cpx1,
          shape.cpy1 +
            (shape.x1 > shape.x2 ? 0 : shape.dvDirection == 'top' ? -curveHeight : curveHeight),
          shape.cpx2 + extent,
          shape.cpy2 +
            (shape.x1 > shape.x2 ? 0 : shape.dvDirection == 'top' ? -curveHeight : curveHeight),
          shape.x2 + extent,
          shape.y2
        );
        ctx.lineTo(shape.x2, shape.y2);
        ctx.bezierCurveTo(
          shape.cpx2,
          shape.cpy2 +
            (shape.x1 > shape.x2 ? (shape.dvDirection == 'top' ? -curveHeight : curveHeight) : 0),
          shape.cpx1 + extent,
          shape.cpy1 +
            (shape.x1 > shape.x2 ? (shape.dvDirection == 'top' ? -curveHeight : curveHeight) : 0),
          shape.x1 + extent,
          shape.y1
        );
      } else {
        ctx.moveTo(shape.x1, shape.y1);
        ctx.bezierCurveTo(
          shape.cpx1 +
            (shape.y1 > shape.y2 ? 0 : shape.dvDirection == 'top' ? -curveHeight : curveHeight),
          shape.cpy1,
          shape.cpx2 +
            (shape.y1 > shape.y2 ? 0 : shape.dvDirection == 'top' ? -curveHeight : curveHeight),
          shape.cpy2 + extent,
          shape.x2,
          shape.y2 + extent
        );
        ctx.lineTo(shape.x2, shape.y2);
        ctx.bezierCurveTo(
          shape.cpx2 +
            (shape.y1 > shape.y2 ? (shape.dvDirection == 'top' ? -curveHeight : curveHeight) : 0),
          shape.cpy2,
          shape.cpx1 +
            (shape.y1 > shape.y2 ? (shape.dvDirection == 'top' ? -curveHeight : curveHeight) : 0),
          shape.cpy1 + extent,
          shape.x1,
          shape.y1 + extent
        );
      }
      ctx.closePath();
    } else {
      const { extent } = shape;
      ctx.moveTo(shape.x1, shape.y1);
      ctx.bezierCurveTo(shape.cpx1, shape.cpy1, shape.cpx2, shape.cpy2, shape.x2, shape.y2);
      if (shape.orient === 'vertical') {
        ctx.lineTo(shape.x2 + extent, shape.y2);
        ctx.bezierCurveTo(
          shape.cpx2 + extent,
          shape.cpy2,
          shape.cpx1 + extent,
          shape.cpy1,
          shape.x1 + extent,
          shape.y1
        );
      } else {
        ctx.lineTo(shape.x2, shape.y2 + extent);
        ctx.bezierCurveTo(
          shape.cpx2,
          shape.cpy2 + extent,
          shape.cpx1,
          shape.cpy1 + extent,
          shape.x1,
          shape.y1 + extent
        );
      }
      ctx.closePath();
    }
  }

  highlight() {
    enterEmphasis(this);
  }

  downplay() {
    leaveEmphasis(this);
  }
}

class SankeyTreeView extends SankeyView {
  static readonly type = 'sankey';

  readonly type = SankeyView.type;

  private _model: SankeySeriesModel;

  private _focusAdjacencyDisabled = false;

  private _data: SeriesData;

  private _nodeData: SeriesData;

  private _edgeData: SeriesData;

  render(seriesModel: SankeySeriesModel, ecModel: GlobalModel, api: ExtensionAPI) {
    if (seriesModel.get('dvLayout') && seriesModel.get('dvLayout') === 'tree') {
      const sankeyView = this;
      const graph = seriesModel.getGraph();
      const { group } = this;
      const { layoutInfo } = seriesModel;
      // view width
      const { width } = layoutInfo;
      // view height
      const { height } = layoutInfo;
      const nodeData = seriesModel.getData();
      const edgeData = seriesModel.getData('edge');
      const oldNodeData = this._nodeData;
      const oldEdgeData = this._edgeData;
      const orient = seriesModel.get('orient');
      const edgeSilent = seriesModel.get('dvEdgeSilent');

      if (!oldNodeData || !oldEdgeData) {
        group.removeAll();
      }
      if (orient === 'horizontal') {
        const viewContainer = new graphic.Rect({
          silent: true,
          z: -1,
          x: 0,
          y: 0,
          shape: {
            width: width + seriesModel.get('left') + seriesModel.get('right'),
            height
          },
          style: {
            fill: 'rgba(255,255,255,0)',
            opacity: 0
          }
        });
        group.add(viewContainer);
      }
      graph.edgeData
        .diff(oldEdgeData)
        .add(function (newIdx) {
          const edge = graph.edges[newIdx];
          drawEdge(edge);

          // 旧数据存在，若diff到这里，表明是新增的
          if (oldEdgeData) {
            // 获取新数据点的图形元素
            const symbolEl = graph.edgeData.getItemGraphicEl(newIdx);
            // 保存新数据位置信息
            const target = {
              shape: JSON.parse(JSON.stringify(symbolEl.shape)),
              style: JSON.parse(JSON.stringify(symbolEl.style))
            };
            // 将新数据的extent置为0，动画需要
            symbolEl.shape.extent = 0;
            symbolEl.style.opacity = 0;
            // 通过动画更新元素的extent
            graphic.updateProps(symbolEl, target, seriesModel);
            group.add(symbolEl);
            edgeData.setItemGraphicEl(edge.dataIndex, symbolEl);
          }
        })
        .update(function (newIdx, oldIdx) {
          const edge = graph.edges[newIdx];
          drawEdge(edge);
          // 获取旧数据点的图形元素
          const symbolEl = oldEdgeData.getItemGraphicEl(oldIdx);
          // 获取新数据点的图形元素
          const point = graph.edgeData.getItemGraphicEl(newIdx);
          // 获取新数据点的图形绘制信息
          const target = {
            shape: point.shape
          };

          // 通过动画更新符号元素的位置
          graphic.updateProps(symbolEl, target, seriesModel);
          group.remove(point);
          group.add(symbolEl);
          edgeData.setItemGraphicEl(edge.dataIndex, symbolEl);
        })
        .remove(function (oldIdx) {
          const edge = graph.edges[oldIdx];
          // 获取旧数据点的图形元素
          const oldSymbolEl = oldEdgeData.getItemGraphicEl(oldIdx);
          // 获取新数据点的图形绘制信息
          const target = {
            shape: {
              extent: 0
            },
            style: {
              opacity: 0
            }
          };
          // 通过动画更新符号元素的位置
          graphic.updateProps(oldSymbolEl, target, seriesModel, null, () =>
            group.remove(oldSymbolEl)
          );
          group.add(oldSymbolEl);
        })
        .execute();

      graph.data
        .diff(oldNodeData)
        .add(function (newIdx) {
          const node = graph.nodes[newIdx];
          drawNode(node);

          // 旧数据存在，若diff到这里，表明是新增的
          if (oldNodeData) {
            // 获取新数据点的图形元素
            const symbolEl = graph.data.getItemGraphicEl(newIdx);
            // 保存新数据位置信息
            const target = {
              shape: JSON.parse(JSON.stringify(symbolEl.shape))
            };
            // 将新数据的width置为0，动画需要
            if (orient === 'vertical') {
              symbolEl.shape.width = 0;
            } else {
              symbolEl.shape.height = 0;
            }

            // 通过动画更新元素的width
            graphic.updateProps(symbolEl, target, seriesModel);
            group.add(symbolEl);
            nodeData.setItemGraphicEl(node.dataIndex, symbolEl);
          }
        })
        .update(function (newIdx, oldIdx) {
          const node = graph.nodes[newIdx];
          drawNode(node);
          // 获取旧数据点的图形元素
          const oldSymbolEl = oldNodeData.getItemGraphicEl(oldIdx);
          // 获取新数据点的图形元素
          const symbolEl = graph.data.getItemGraphicEl(newIdx);
          // 获取新数据点的图形绘制信息
          const target = {
            shape: JSON.parse(JSON.stringify(symbolEl.shape))
          };
          symbolEl.shape = JSON.parse(JSON.stringify(oldSymbolEl.shape));

          const oldInfo = oldNodeData.getRawDataItem(oldIdx);
          const newInfo = graph.data.getRawDataItem(newIdx);
          setLabelStyle(symbolEl, getLabelStatesModels(node.getModel()), {
            labelFetcher: {
              // 获取label格式化之后的文字
              getFormattedLabel(
                labelDataIndex,
                status,
                dataType,
                labelDimIndex,
                formatter,
                extendParams
              ) {
                return seriesModel.getFormattedLabel(
                  labelDataIndex,
                  status,
                  'node',
                  labelDimIndex,
                  formatter,
                  extendParams
                );
              }
            },
            labelDataIndex: node.dataIndex,
            // 允许设置label的文字内容
            enableTextSetter: true
          });
          group.remove(oldSymbolEl);
          
          nodeData.setItemGraphicEl(node.dataIndex, symbolEl);

          const encode = seriesModel.get('dvEncode') as string[];

          // 通过动画更新符号元素的位置
          graphic.updateProps(symbolEl, target, seriesModel, {
            during(percent) {
              if (!sankeyView._data || !seriesModel.get(['label', 'valueAnimation'])) {
                return;
              }

              const inner = labelInner(symbolEl.getTextContent());

              if (typeof inner.setLabelText === 'function' && Array.isArray(encode)) {
                const valueOnFrame = encode.map(dim => {
                  if (isNaN(+oldInfo[dim]) || isNaN(+newInfo[dim])) {
                    return undefined;
                  }
                  const res = (newInfo[dim] - oldInfo[dim]) * percent + oldInfo[dim];
                  return percent === 1 ? newInfo[dim] : res.toFixed(1);
                });
                inner.setLabelText(valueOnFrame);
              }
            }
          });
        })
        .remove(function (oldIdx) {
          // 获取旧数据点的图形元素
          const oldSymbolEl = oldNodeData.getItemGraphicEl(oldIdx);
          // 获取新数据点的图形绘制信息
          let target;
          if (orient === 'vertical') {
            target = {
              shape: {
                width: 0
              },
              _textContent: {
                style: {
                  opacity: 0
                }
              }
            };
          } else {
            target = {
              shape: {
                height: 0
              },
              _textContent: {
                style: {
                  opacity: 0
                }
              }
            };
          }
          // 通过动画更新符号元素的位置
          graphic.updateProps(oldSymbolEl, target, seriesModel, null, () =>
            group.remove(oldSymbolEl)
          );
        })
        .execute();
      this._nodeData = graph.data;
      this._edgeData = graph.edgeData;
      function drawEdge(edge) {
        const curve = new SankeyPath();
        const ecData = getECData(curve);
        ecData.dataIndex = edge.dataIndex;
        ecData.seriesIndex = seriesModel.seriesIndex;
        ecData.dataType = 'edge';
        const edgeModel = edge.getModel<SankeyEdgeItemOption>();
        const lineStyleModel = edgeModel.getModel('lineStyle');
        const curvature = lineStyleModel.get('curveness');
        const n1Layout = edge.node1.getLayout();
        const node1Model = edge.node1.getModel<SankeyNodeItemOption>();
        const dragX1 = node1Model.get('localX');
        const dragY1 = node1Model.get('localY');
        const n2Layout = edge.node2.getLayout();
        const node2Model = edge.node2.getModel<SankeyNodeItemOption>();
        const dragX2 = node2Model.get('localX');
        const dragY2 = node2Model.get('localY');
        const edgeLayout = edge.getLayout();
        const controlPointDvalue = 10;
        let x1: number;
        let y1: number;
        let x2: number;
        let y2: number;
        let cpx1: number;
        let cpy1: number;
        let cpx2: number;
        let cpy2: number;
        curve.shape.extent = Math.max(1, edgeLayout.dy);
        curve.shape.orient = orient;
        curve.shape.dvKind = 'normal';
        if (edge.node1.getLayout().depth == edge.node2.getLayout().depth) {
          curve.shape.dvKind = 'sameLayerCurve';
          edge.getModel().option.dvDirection &&
            (curve.shape.dvDirection = edge.getModel().option.dvDirection);
          if (orient === 'vertical') {
            x1 = (dragX1 != null ? dragX1 * width : n1Layout.x) + edgeLayout.sy;
            y1 = (dragY1 != null ? dragY1 * height : n1Layout.y) + n1Layout.dy;
            x2 = (dragX2 != null ? dragX2 * width : n2Layout.x) + edgeLayout.ty;
            y2 = dragY2 != null ? dragY2 * height : n2Layout.y;
            cpx1 = x1;
            cpx2 = x2;
            if (curve.shape.dvDirection == 'top') {
              y1 = y2;
              cpy1 = y2 - controlPointDvalue;
              cpy2 = y2 - controlPointDvalue;
            } else {
              y2 = y1;
              cpy1 = y1 + controlPointDvalue;
              cpy2 = y1 + controlPointDvalue;
            }
          } else {
            x1 = (dragX1 != null ? dragX1 * width : n1Layout.x) + n1Layout.dx;
            y1 = (dragY1 != null ? dragY1 * height : n1Layout.y) + edgeLayout.sy;
            x2 = dragX2 != null ? dragX2 * width : n2Layout.x;
            y2 = (dragY2 != null ? dragY2 * height : n2Layout.y) + edgeLayout.ty;
            cpy1 = y1;
            cpy2 = y2;
            if (curve.shape.dvDirection == 'top') {
              x1 = x2;
              cpx1 = x2 - controlPointDvalue;
              cpx2 = x2 - controlPointDvalue;
            } else {
              x2 = x1;
              cpx1 = x1 + controlPointDvalue;
              cpx2 = x1 + controlPointDvalue;
            }
          }

          curve.setShape({
            x1: x1 + layoutInfo.x,
            // 处理zrender中两条贝塞尔曲线闭合区域，当四个点Y方向高度一致时出现的区域判定bug问题
            y1: y1 + layoutInfo.y - 0.01,
            x2: x2 + layoutInfo.x,
            y2: y2 + layoutInfo.y,
            cpx1: cpx1 + layoutInfo.x,
            cpy1: cpy1 + layoutInfo.y,
            cpx2: cpx2 + layoutInfo.x,
            cpy2: cpy2 + layoutInfo.y
          });
        } else {
          if (orient === 'vertical') {
            x1 = (dragX1 != null ? dragX1 * width : n1Layout.x) + edgeLayout.sy;
            y1 = (dragY1 != null ? dragY1 * height : n1Layout.y) + n1Layout.dy;
            x2 = (dragX2 != null ? dragX2 * width : n2Layout.x) + edgeLayout.ty;
            y2 = dragY2 != null ? dragY2 * height : n2Layout.y;
            cpx1 = x1;
            cpy1 = y1 * (1 - curvature) + y2 * curvature;
            cpx2 = x2;
            cpy2 = y1 * curvature + y2 * (1 - curvature);
          } else {
            x1 = (dragX1 != null ? dragX1 * width : n1Layout.x) + n1Layout.dx;
            y1 = (dragY1 != null ? dragY1 * height : n1Layout.y) + edgeLayout.sy;
            x2 = dragX2 != null ? dragX2 * width : n2Layout.x;
            y2 = (dragY2 != null ? dragY2 * height : n2Layout.y) + edgeLayout.ty;
            cpx1 = x1 * (1 - curvature) + x2 * curvature;
            cpy1 = y1;
            cpx2 = x1 * curvature + x2 * (1 - curvature);
            cpy2 = y2;
          }

          curve.setShape({
            x1: x1 + layoutInfo.x,
            y1: y1 + layoutInfo.y,
            x2: x2 + layoutInfo.x,
            y2: y2 + layoutInfo.y,
            cpx1: cpx1 + layoutInfo.x,
            cpy1: cpy1 + layoutInfo.y,
            cpx2: cpx2 + layoutInfo.x,
            cpy2: cpy2 + layoutInfo.y
          });
        }
        curve.useStyle(lineStyleModel.getItemStyle());
        // Special color, use source node color or target node color
        switch (curve.style.fill) {
          case 'source':
            curve.style.fill = edge.node1.getVisual('color');
            curve.style.decal = edge.node1.getVisual('style').decal;
            break;
          case 'target':
            curve.style.fill = edge.node2.getVisual('color');
            curve.style.decal = edge.node2.getVisual('style').decal;
            break;
          case 'gradient':
            const sourceColor = edge.node1.getVisual('color');
            const targetColor = edge.node2.getVisual('color');
            if (isString(sourceColor) && isString(targetColor)) {
              curve.style.fill = new graphic.LinearGradient(
                0,
                0,
                +(orient === 'horizontal'),
                +(orient === 'vertical'),
                [
                  {
                    color: sourceColor,
                    offset: 0
                  },
                  {
                    color: targetColor,
                    offset: 1
                  }
                ]
              );
            }
        }

        setLabelStyle(curve, getLabelStatesModels(edgeModel, 'edgeLabel'), {
          labelFetcher: seriesModel,
          labelDataIndex: edge.dataIndex,
          defaultText: `${edgeModel.get('value')}`
        });
        curve.setTextConfig({ position: 'inside' });

        const emphasisModel = edgeModel.getModel('emphasis');

        setStatesStylesFromModel(curve, edgeModel, 'lineStyle', model => model.getItemStyle());

        group.add(curve);

        edgeData.setItemGraphicEl(edge.dataIndex, curve);

        const focus = emphasisModel.get('focus');
        // debugger;
        toggleHoverEmphasis(
          curve,
          focus === 'adjacency' ? edge.getAdjacentDataIndices() : focus,
          emphasisModel.get('blurScope'),
          emphasisModel.get('disabled')
        );

        // ! 禁止鼠标事件响应不会影响 Emphasis / Blur 等状态的切换，只不过由 Node 的交互触发
        // ! 禁止响应事件后会影响 tooltip 的展示更新，建议仅在移动端使用该配置项
        curve.silent = !!edgeSilent;

        getECData(curve).dataType = 'edge';
      }
      // Generate a rect for each node
      function drawNode(node) {
        const layout = node.getLayout();
        const itemModel = node.getModel<SankeyNodeItemOption>();
        const dragX = itemModel.get('localX');
        const dragY = itemModel.get('localY');
        const emphasisModel = itemModel.getModel('emphasis');

        const rect = new graphic.Rect({
          x: layoutInfo.x,
          y: layoutInfo.y,
          shape: {
            x: dragX != null ? dragX * width : layout.x,
            y: dragY != null ? dragY * height : layout.y,
            width: layout.dx,
            height: layout.dy
          },
          style: itemModel.getModel('itemStyle').getItemStyle(),
          z2: 10
        });

        setLabelStyle(rect, getLabelStatesModels(itemModel), {
          labelFetcher: seriesModel,
          labelDataIndex: node.dataIndex,
          defaultText: node.id
        });

        (rect as ECElement).disableLabelAnimation = true;

        rect.setStyle('fill', node.getVisual('color'));
        rect.setStyle('decal', node.getVisual('style').decal);

        setStatesStylesFromModel(rect, itemModel);

        group.add(rect);

        nodeData.setItemGraphicEl(node.dataIndex, rect);

        getECData(rect).dataType = 'node';

        const focus = emphasisModel.get('focus');
        toggleHoverEmphasis(
          rect,
          focus === 'adjacency' ? node.getAdjacentDataIndices() : focus,
          emphasisModel.get('blurScope'),
          emphasisModel.get('disabled')
        );
      }

      nodeData.eachItemGraphicEl(function (el: graphic.Rect, dataIndex: number) {
        const itemModel = nodeData.getItemModel<SankeyNodeItemOption>(dataIndex);
        if (itemModel.get('draggable')) {
          el.drift = function (this: typeof el, dx, dy) {
            sankeyView._focusAdjacencyDisabled = true;
            this.shape.x += dx;
            this.shape.y += dy;
            this.dirty();
            api.dispatchAction({
              type: 'dragNode',
              seriesId: seriesModel.id,
              dataIndex: nodeData.getRawIndex(dataIndex),
              localX: this.shape.x / width,
              localY: this.shape.y / height
            });
          };
          el.ondragend = function () {
            sankeyView._focusAdjacencyDisabled = false;
          };
          el.draggable = true;
          el.cursor = 'move';
        }
      });

      if (!this._data && seriesModel.isAnimationEnabled()) {
        group.setClipPath(
          createGridClipShape(group.getBoundingRect(), seriesModel, function () {
            group.removeClipPath();
          })
        );
      }

      this._data = seriesModel.getData();
    } else {
      super.render(seriesModel, ecModel, api);
    }
  }

  dispose() {}
}

// Add animation to the view
function createGridClipShape(rect: RectLike, seriesModel: SankeySeriesModel, cb: () => void) {
  const rectEl = new graphic.Rect({
    shape: {
      x: seriesModel.option.orient === 'vertical' ? rect.x - 35 : rect.x - 105,
      y: rect.y - 35,
      width:
        seriesModel.option.orient === 'vertical'
          ? seriesModel.layoutInfo.width + seriesModel.option.left + seriesModel.option.right
          : 0,
      height:
        seriesModel.option.orient === 'vertical'
          ? 0
          : seriesModel.layoutInfo.height + seriesModel.option.top + seriesModel.option.bottom
    }
  });
  const target =
    seriesModel.option.orient === 'vertical'
      ? {
          height: rect.height + 70
        }
      : {
          width: rect.width + 105
        };
  graphic.initProps(
    rectEl,
    {
      shape: target
    },
    seriesModel,
    cb
  );
  return rectEl;
}

export default SankeyTreeView;
